import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  Clock, 
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { ProviderLevelBadge } from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reliable placeholder image
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

/**
 * ServiceCardStandard (Grid Card A) - COMPACT VERSION
 * Standard service card for product grids - shows service provider with basic info
 * Used for: Alterations & Tailoring, Dry Cleaning, etc.
 */
const ServiceCardStandard = ({ 
  service, 
  onBook, 
  onViewDetails,
  className = '' 
}) => {
  const {
    id,
    provider_name,
    provider_image,
    service_name,
    rating = 0,
    review_count = 0,
    starting_price,
    currency = 'Rs',
    distance,
    response_time,
    verification_level = 0,
    is_sponsored = false,
  } = service;

  const imageUrl = provider_image 
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : PLACEHOLDER_IMAGE;

  return (
    <Card 
      className={`group hover:shadow-lg transition-all duration-200 overflow-hidden border hover:border-purple-400 cursor-pointer ${className}`}
      onClick={() => onViewDetails?.(service)}
      data-testid={`service-card-${id}`}
    >
      {/* Sponsored Badge */}
      {is_sponsored && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          <span>Sponsored</span>
        </div>
      )}
      
      <CardContent className="p-3">
        {/* Header: Provider Info - Compact */}
        <div className="flex items-center gap-2.5 mb-2">
          <img
            src={imageUrl}
            alt={provider_name}
            className="w-11 h-11 rounded-full object-cover border-2 border-purple-200 flex-shrink-0"
            onError={(e) => {
              e.target.src = PLACEHOLDER_IMAGE;
            }}
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate group-hover:text-purple-600 transition-colors text-sm">
              {provider_name}
            </h3>
            {/* Service name with rating inline */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 truncate">{service_name}</span>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                <span className="text-xs font-semibold">{rating.toFixed(1)}</span>
                <span className="text-xs text-gray-400">({review_count})</span>
              </div>
            </div>
            <ProviderLevelBadge level={verification_level} size="xs" />
          </div>
        </div>
        
        {/* Quick Info - Compact */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
          {distance && (
            <div className="flex items-center gap-0.5">
              <MapPin className="h-3 w-3" />
              <span>{distance}</span>
            </div>
          )}
          {response_time && (
            <div className="flex items-center gap-0.5">
              <Clock className="h-3 w-3" />
              <span>{response_time}</span>
            </div>
          )}
        </div>
        
        {/* Price & CTA - Compact */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div>
            <p className="text-xs text-gray-400">Starting from</p>
            <p className="text-sm font-bold text-purple-600">
              {currency} {starting_price?.toLocaleString()}
            </p>
          </div>
          <Button 
            size="sm"
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-8 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onBook?.(service);
            }}
            data-testid={`book-service-${id}`}
          >
            Book Now
            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceCardStandard;
